// src/views/InvoicesView.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'; // colpick-8a2c4f19
import { getFunctions, httpsCallable } from 'firebase/functions';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom'; 
import { db } from '../firebase/config';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore'; // colpick-4a1e9b73
import { Plus, ArrowUp, ArrowDown, XCircle, Search, Calendar, User, DollarSign, CheckSquare, Square, Send, ChevronDown, Archive, CheckCircle, ArrowUpRight, AlertTriangle, Columns3 /* qbcol-4e8a1c62 paysynclistimp-6a4c2e19 colpick-7c3d5f28 */ } from 'lucide-react'; 
import InvoiceStatusBadge from '../components/common/InvoiceStatusBadge';
import { formatCurrency, formatDate, getInvoicePaidDate, deriveInvoiceRowFields, isPaymentSyncWarningActive } from '../utils/formatters'; // r3-paiddate qbcol-9d3f57b1 strip-b2d5f108 paysynclistimp-6a4c2e19
import { canSeeAllCustomers, canBatchInvoice } from '../utils/permissions'; // officecust-9a4d2c61; canBatchInvoice: s8portal-4e7b2c95
import { qbTotalMismatchOf, qbTotalMismatchText } from '../utils/qbTotalMismatch'; // qbpushcheck-7e2c5a94 (row 31 T1)

// qbpushcheck-7e2c5a94 -- row 31 T1: the badge words for an invoice whose QuickBooks total/tax differs (null otherwise).
const qbMismatchWordsFor = (invoice) => { const m = qbTotalMismatchOf(invoice); return m ? qbTotalMismatchText(m, (n) => formatCurrency(n)) : null; };

// renderQbStateBadge: shared cell content for the invoice-list "QB" column so the
// desktop table and mobile card render identically. Slate "In QuickBooks" (origin)
// and green "Synced" mirror the detail-view badges (qbguard-9e07c3d1); 'none' is a
// muted em-dash, deliberately not a badge. qbcol-2c6b90fa
const renderQbStateBadge = (qbPushState, qbPushError, qbMismatchWords = null) => {
    if (qbPushState === 'origin') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded-md font-medium border border-slate-300">
                <Archive size={12} /> In QuickBooks
            </span>
        );
    }
    if (qbPushState === 'synced') {
        /* qbpushcheck-7e2c5a94 -- row 31 T1: in QuickBooks, but with a different total or tax
           than was billed. Amber, figures on hover; without the marker the green badge below. */
        if (qbMismatchWords) {
            return (
                <span title={qbMismatchWords.badgeTitle} className="inline-flex items-center gap-1 text-xs text-amber-800 bg-amber-100 px-2 py-1 rounded-md font-medium border border-amber-300">
                    <AlertTriangle size={12} /> {qbMismatchWords.badge}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-md font-medium border border-green-200">
                <CheckCircle size={12} /> Synced
            </span>
        );
    }
    if (qbPushState === 'failed') {
        /* qbfail-9d54c7e1 -- reason via title only here (desktop hover); the
           mobile card renders it inline because touch has no hover. */
        return (
            <span title={qbPushError || 'Push failed'} className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-1 rounded-md font-medium border border-red-200">
                <XCircle size={12} /> Failed
            </span>
        );
    }
    if (qbPushState === 'deferred') {
        /* qbdefer-7b2e5d41 -- Seam A: automatic sync is WAITING, not failed. Amber
           (not red) because nothing broke -- the invoice couldn't auto-sync because
           the QuickBooks connection needs attention or accounts aren't configured,
           and it will sync once that's fixed (or on a manual Export). Distinct tone
           from the red Failed badge on purpose. */
        return (
            <span title="Waiting to sync — QuickBooks connection needs attention" className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-md font-medium border border-amber-200">
                <AlertTriangle size={12} /> Waiting to sync
            </span>
        );
    }
    return <span className="text-gray-400">{'—'}</span>;
};

// --- qbbulk-f7d21e83 -- computeQbPushEligibility ------------------------------
// MIRROR qbmirror-6a1d93e5: rules, ORDER, and reason strings must stay in sync
// with the precheck loop in batchPushInvoicesToQuickBooks (functions/index.js).
// The server is authoritative; this client copy exists so the confirmation
// modal can predict exactly what the server will decide. Rows 1-2 mirror the
// server's isQuickBooksOriginInvoice / qbInvoiceId checks via qbPushState,
// which is derived from those same two predicates (qbcol-7f1e34a8 below).
// Returns { eligible: true, warning } or { eligible: false, reason }.
const computeQbPushEligibility = (invoice) => {
    if (invoice.qbPushState === 'origin') return { eligible: false, reason: 'Imported from QuickBooks' };
    if (invoice.qbPushState === 'synced') return { eligible: false, reason: 'Already synced' };
    const customerName = invoice.customerName || invoice.customerDetails?.name || '';
    if (!String(customerName).trim()) return { eligible: false, reason: 'No customer name' };
    const status = String(invoice.status || '').toLowerCase();
    if (['draft', 'void', 'cancelled', 'canceled', 'written off'].includes(status)) return { eligible: false, reason: 'Draft or voided' }; // s0bwoff-f74fb923
    const customerEmail = invoice.customerDetails?.email || invoice.customerEmail || '';
    return { eligible: true, warning: String(customerEmail).trim() ? null : 'No customer email - matched by name in QuickBooks' };
};

const QB_PUSH_CHUNK_SIZE = 10;
// The 50 cap applies to the PUSH ACTION only. Selection and email bulk send
// stay uncapped -- never gate the shared checkboxes on QB eligibility.
const QB_PUSH_MAX = 50;

// qbattn-4e8a2d56 -- single predicate for the "Needs QB attention" filter: an invoice needs
// action in QuickBooks if a push FAILED or a recorded payment never synced. Two distinct facts,
// one actionable question. Reads invoice.qbPushState (the mapper's view-only row field, already
// present) and the raw payment-sync fields via the shared helper. Module scope, computed at
// filter time -- NOT a derived row field, never written back.
// qbdefer-9c4f6a83 -- Seam A folds the 'deferred' state (auto-sync blocked on a
// recoverable condition) into the same filter so a waiting invoice is findable, not
// just a failed one. Degrades safely: pre-auto-sync invoices never carry the marker,
// so qbPushState is never 'deferred' for them.
const needsQbAttention = (invoice) => invoice?.qbPushState === 'failed' || invoice?.qbPushState === 'deferred' || isPaymentSyncWarningActive(invoice);

// colcfg-7a3e9f21 -- config-driven invoice table (Seam 1). The desktop <thead>
// and <tbody> both render from this ONE array so a column is defined once.
// INVARIANTS this seam must preserve (no visible change is the whole point):
//   * thClass/tdClass hold the ORIGINAL className tokens, character-identical.
//   * cursor-pointer + hover:bg-gray-100 are NOT in thClass -- they are the
//     sortable affordance, appended by the header map ONLY when col.sortKey is
//     set. A future non-sortable column (sortKey: null) therefore renders a bare
//     <th> with no pointer, no hover, no onClick, no sort icon -- impossible to
//     get wrong from the descriptor side.
//   * tdClass may be a string OR a function of the invoice (Balance is the only
//     value-dependent class: orange when owed, green when not).
//   * renderCell returns a node (text, a badge component, or composed JSX).
//   * available(ctx) gates STRUCTURAL visibility (QB column needs a connection),
//     kept separate from any user hide preference (that arrives in Seam 2).
// The batch-selection checkbox column is deliberately NOT here: it has no label,
// its header is a select-all button, its cells stopPropagation, and it is gated
// on batchMode, not a preference. It stays rendered explicitly in the JSX.
// Seam 7 -- subtle chip flagging that a row's customer has available account
// credit, so an operator sees it in the list without opening the invoice.
const CreditChip = () => (
    <span
        className="ml-2 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 align-middle"
        title="This customer has available account credit"
    >
        Credit
    </span>
);

const COLUMNS = [
    { id: 'invoiceNumber', label: 'Inv #', sortKey: 'invoiceNumber', locked: true, // colpicklk-2d6b8f41 -- identifying columns, never hideable (Inv #/Customer/Total)
        thClass: 'px-6 py-3',
        tdClass: 'px-6 py-4 font-medium text-gray-900',
        renderCell: (invoice) => invoice.invoiceNumber },
    { id: 'customerName', label: 'Customer', sortKey: 'customerName', locked: true,
        thClass: 'px-6 py-3',
        tdClass: 'px-6 py-4 font-medium',
        renderCell: (invoice, ctx) => (
            <>
                {invoice.customerDetails?.name || invoice.customerName}
                {ctx?.creditCustomerIds?.has(invoice.customerId) && <CreditChip />}
            </>
        ) },
    { id: 'createdAt', label: 'Date', sortKey: 'createdAt',
        thClass: 'px-6 py-3',
        tdClass: 'px-6 py-4',
        renderCell: (invoice) => formatDate(invoice.createdAt) },
    { id: 'dueDate', label: 'Due', sortKey: 'dueDate',
        thClass: 'px-6 py-3',
        tdClass: 'px-6 py-4',
        renderCell: (invoice) => formatDate(invoice.dueDate) },
    { id: 'paidDate', label: 'Date Paid', sortKey: 'paidDate',   // r3-paiddate
        thClass: 'px-6 py-3',
        tdClass: 'px-6 py-4',
        renderCell: (invoice) => invoice.paidDate ? formatDate(invoice.paidDate) : '\u2014' },
    { id: 'total', label: 'Total', sortKey: 'total', locked: true,
        thClass: 'px-6 py-3 text-right',
        tdClass: 'px-6 py-4 text-right',
        renderCell: (invoice) => formatCurrency(invoice.total) },
    { id: 'amountPaid', label: 'Paid', sortKey: 'amountPaid',    // r3-paiddate
        thClass: 'px-6 py-3 text-right',
        tdClass: 'px-6 py-4 text-right text-green-600',
        renderCell: (invoice) => formatCurrency(invoice.amountPaid) },
    { id: 'balanceDue', label: 'Balance', sortKey: 'balanceDue',
        thClass: 'px-6 py-3 text-right',
        tdClass: (invoice) => `px-6 py-4 text-right font-bold ${invoice.balanceDue > 0 ? 'text-orange-600' : 'text-green-600'}`,
        renderCell: (invoice) => formatCurrency(invoice.balanceDue) },
    { id: 'status', label: 'Status', sortKey: 'status',
        thClass: 'px-6 py-3 text-center',
        tdClass: 'px-6 py-4 text-center',
        renderCell: (invoice) => <InvoiceStatusBadge status={invoice.status} /> },
    // qbcol-b58c0d29 qbcol-3ea7f6c4 -- QB column; available only when QB connected.
    { id: 'qbPushState', label: 'QB', sortKey: 'qbPushState',
        available: (ctx) => ctx.companySettings?.quickbooksConnected,
        thClass: 'px-6 py-3 text-center',
        tdClass: 'px-6 py-4 text-center',
        renderCell: (invoice) => (
            // paysynccell-5e1b8d40 -- QB push badge + a distinct amber payment-sync
            // flag. Separate fact from qbPushState: an invoice can be Synced and
            // still carry an unsynced-payment mark.
            <div className="flex items-center justify-center gap-1.5">
                {renderQbStateBadge(invoice.qbPushState, invoice.qbPushError, qbMismatchWordsFor(invoice))}
                {isPaymentSyncWarningActive(invoice) && (
                    <span title="A recorded payment has not reached QuickBooks">
                        <AlertTriangle size={14} className="text-amber-500" />
                    </span>
                )}
            </div>
        ) },
];

const InvoicesView = ({ currentUserProfile, onInvoiceSelect, openInvoiceModal, companySettings }) => {
    const [invoices, setInvoices] = useState([]);
    const [creditCustomerIds, setCreditCustomerIds] = useState(() => new Set()); // Seam 7 — customers with open, positive credit
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // --- DATE RANGE FILTER ---
    const [datePreset, setDatePreset] = useState('all');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    // --- STATUS FILTER (multi-select) ---
    const [statusFilters, setStatusFilters] = useState(new Set());
    const toggleStatus = (s) => setStatusFilters(prev => {
        const next = new Set(prev);
        if (next.has(s)) next.delete(s); else next.add(s);
        return next;
    });

    // qbattn-7b3f9c21 -- "Needs QB attention" filter. Its OWN boolean state, NOT folded into
    // statusFilters (a QB problem is orthogonal to invoice status). Deliberately NOT persisted
    // (unlike sortConfig) so a reload never leaves someone staring at a silently filtered list.
    const [qbAttentionOnly, setQbAttentionOnly] = useState(false);

    // --- BATCH SELECTION ---
    const [batchMode, setBatchMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [batchSendModal, setBatchSendModal] = useState(false);
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchResults, setBatchResults] = useState(null);

    // --- QB BULK PUSH STATE (qbbulk-9a4c60d5) ---
    const [qbPushConfirm, setQbPushConfirm] = useState(null);   // { eligible, skipped, warnCount, overCap }
    const [qbPushing, setQbPushing] = useState(false);
    const [qbPushProgress, setQbPushProgress] = useState(null); // { done, total }
    const [qbPushResults, setQbPushResults] = useState(null);   // { pushed, skipped, failed }

    // --- DEEP LINKING SETUP ---
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const filterParam = queryParams.get('filter'); // e.g., "Unpaid"

    // --- 1. STICKY SORT CONFIG ---
    const [sortConfig, setSortConfig] = useState(() => {
        const savedSort = localStorage.getItem('invoiceSortConfig');
        return savedSort 
            ? JSON.parse(savedSort) 
            : { key: 'invoiceNumber', direction: 'descending' };
    });

    // --- 2. SAVE SORT ON CHANGE ---
    useEffect(() => {
        localStorage.setItem('invoiceSortConfig', JSON.stringify(sortConfig));
    }, [sortConfig]);

    // --- COLUMN PICKER (colpick-9b5e1a06) ------------------------------------
    // These hooks sit between the SAVE-SORT effect above and the FETCH-DATA effect
    // below -- i.e. in the hooks block at the top of the component, ~230 lines above
    // the `if (isLoading) return` early return (line 433), so rules-of-hooks holds.
    // hiddenIds seeds ONCE from the profile (lazy initializer). Safe because
    // ProtectedCrmLayout gates the whole CRM on (user && currentUserProfile) before
    // this view mounts and applyProfile always carries columnPrefs -- the saved value
    // is present at first render, never arriving late. Local state is AUTHORITATIVE
    // thereafter: there is deliberately NO profile->local sync effect, so a toggle's
    // own write echoing back through the snapshot cannot clobber it and nothing can
    // oscillate. (A change on another device shows on next reload, not live.)
    const [hiddenIds, setHiddenIds] = useState(() => new Set(currentUserProfile?.columnPrefs?.invoiceList || []));
    const [columnMenuOpen, setColumnMenuOpen] = useState(false);
    const [columnSaveError, setColumnSaveError] = useState(false);
    const columnMenuRef = useRef(null);

    // Background write. Hidden ids are serialized in COLUMNS DECLARATION ORDER
    // (COLUMNS.filter -> map), NOT Array.from(hiddenIds) which would be insertion
    // order and unstable across toggles -- stable order makes the snapshot echo
    // stringify identically so applyProfile's dedup skips a re-render. A failed write
    // keeps the local choice and shows an inline note; it never reverts the toggle.
    const persistHidden = (set) => {
        const ordered = COLUMNS.filter(c => set.has(c.id)).map(c => c.id);
        updateDoc(doc(db, 'users', currentUserProfile.id), { 'columnPrefs.invoiceList': ordered })
            .then(() => setColumnSaveError(false))
            .catch(() => setColumnSaveError(true));
    };
    const toggleColumn = (id) => {
        const next = new Set(hiddenIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setHiddenIds(next);
        persistHidden(next);
    };
    const resetColumns = () => {
        const empty = new Set();
        setHiddenIds(empty);
        persistHidden(empty);
    };

    // Outside-click + Escape close, mirroring CustomerTable.js / InvoiceDetailsView.js.
    // The listener exists only while the menu is open; removed on close/unmount.
    useEffect(() => {
        if (!columnMenuOpen) return;
        const onDown = (e) => { if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) setColumnMenuOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setColumnMenuOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [columnMenuOpen]);

    // --- 3. FETCH DATA ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const invoicesPath = `organizations/${currentUserProfile.organizationId}/invoices`;
        
        const q = query(collection(db, invoicesPath), orderBy('createdAt', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allInvoices = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate(),
                    dueDate: data.dueDate?.toDate(),
                    total: Number(data.total || 0),
                    balanceDue: data.balanceDue !== undefined ? Number(data.balanceDue) : Number(data.total || 0),
                    invoiceNumber: data.invoiceNumber || '',
                    // qbPushState (view-only): derived here via the shared helper so the list
                    // and the edit-save strip agree on one source of truth. This key and the
                    // paidDate below are stripped before any invoice write; the leak they caused
                    // is documented at INVOICE_ROW_DERIVED_KEYS in utils/formatters. The old
                    // inline expression and its notes now live in deriveInvoiceRowFields.
                    // qbcol-7f1e34a8 qbfail-9d54c7e1 strip-c3e6a219
                    ...deriveInvoiceRowFields(doc.id, data),
                    paidDate: getInvoicePaidDate(data),        // r3-paiddate — Date | null, from paymentHistory / settledAt
                    amountPaid: Number(data.amountPaid || 0),  // r3-paiddate — normalized for display + sort
                };
            });

            // --- SMART FILTER: SECURITY LOGIC ---
            // officecust-9a4d2c61 — Office Staff + every high-level role see ALL invoices (founder
            // decision 2026-10-04); Sales Consultants see the ones they created.
            const isHighLevel = canSeeAllCustomers(currentUserProfile);

            const visibleInvoices = allInvoices.filter(invoice => {
                if (isHighLevel) return true;
                return invoice.createdBy === currentUserProfile.id;
            });

            setInvoices(visibleInvoices);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching invoices:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUserProfile]);

    // acctcredit-7d1b9e42 -- Seam 7: one org-scoped subscription to customerCredits,
    // reduced to the set of customerIds that hold at least one OPEN, positive-balance
    // credit. Drives the "Credit" chip on the customer column. No per-row reads, no
    // index (whole-collection listener). Errors -> empty set (no chips).
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId) return;
        const creditsQ = collection(db, `organizations/${orgId}/customerCredits`);
        const unsub = onSnapshot(
            creditsQ,
            (snap) => {
                const ids = new Set();
                snap.docs.forEach(d => {
                    const c = d.data();
                    if (c.status === 'open' && Number(c.availableBalance) > 0 && c.customerId) {
                        ids.add(c.customerId);
                    }
                });
                setCreditCustomerIds(ids);
            },
            () => setCreditCustomerIds(new Set())
        );
        return () => unsub();
    }, [currentUserProfile?.organizationId]);

    // --- 4. FILTERING & SORTING ---
    const sortedInvoices = useMemo(() => {
        let processed = [...invoices];

        // Date range filter
        if (datePreset !== 'all') {
            const now = new Date();
            let start, end;
            if (datePreset === 'today') { start = startOfDay(now); end = endOfDay(now); }
            else if (datePreset === 'thisweek') { start = startOfWeek(now); end = endOfWeek(now); }
            else if (datePreset === 'thismonth') { start = startOfMonth(now); end = endOfMonth(now); }
            else if (datePreset === 'custom' && customStart && customEnd) {
                try {
                    start = startOfDay(parseISO(customStart));
                    end = endOfDay(parseISO(customEnd));
                } catch {}
            }
            if (start && end) {
                processed = processed.filter(inv => {
                    const d = inv.createdAt;
                    return d && d >= start && d <= end;
                });
            }
        }

        // Status filter
        if (statusFilters.size > 0) {
            processed = processed.filter(inv => statusFilters.has(inv.status));
        }

        // qbattn-9c1f6b40 -- "Needs QB attention" filter; ANDs with the others (orthogonal axis).
        if (qbAttentionOnly) {
            processed = processed.filter(needsQbAttention);
        }

        // Search Filter (New)
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            processed = processed.filter(inv => 
                inv.invoiceNumber?.toLowerCase().includes(lowerSearch) ||
                inv.customerName?.toLowerCase().includes(lowerSearch) ||
                inv.customerDetails?.name?.toLowerCase().includes(lowerSearch)
            );
        }

        // URL Param Filter
        if (filterParam === 'Unpaid') {
            processed = processed.filter(inv => 
                ['Sent', 'Past Due', 'Unpaid', 'Partial'].includes(inv.status)
            );
        }

        // Sort Logic
        if (sortConfig.key) {
            processed.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle nested customer name sorting
                if (sortConfig.key === 'customerName') {
                    aValue = a.customerDetails?.name || a.customerName || '';
                    bValue = b.customerDetails?.name || b.customerName || '';
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    if (aValue.toLowerCase() < bValue.toLowerCase()) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (aValue.toLowerCase() > bValue.toLowerCase()) return sortConfig.direction === 'ascending' ? 1 : -1;
                } else {
                    if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return processed;
    }, [invoices, sortConfig, filterParam, searchTerm, datePreset, customStart, customEnd, statusFilters, qbAttentionOnly]); // qbattn-2a7d5e93

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <span className="ml-1 inline-block w-4"></span>; 
        return sortConfig.direction === 'ascending' 
            ? <ArrowUp className="inline ml-1 h-4 w-4 text-blue-600" /> 
            : <ArrowDown className="inline ml-1 h-4 w-4 text-blue-600" />;
    };

    const handleSelectInvoice = (id, selected) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (selected) next.add(id); else next.delete(id);
            return next;
        });
    };

    const handleSelectAll = (ids) => setSelectedIds(new Set(ids));

    const handleBatchSend = async () => {
        setBatchLoading(true);
        try {
            const functions = getFunctions();
            const batchSend = httpsCallable(functions, 'batchSendInvoices');
            const result = await batchSend({ invoiceIds: [...selectedIds] });
            setBatchResults(result.data);
        } catch (err) {
            setBatchResults({ sent: [], failed: [{ invoiceId: 'all', reason: err.message }] });
        } finally {
            setBatchLoading(false);
            setBatchSendModal(false);
            setSelectedIds(new Set());
            setBatchMode(false);
        }
    };

    // --- qbbulk-9a4c60d5 -- bulk push to QuickBooks ---------------------------
    // Eligibility is evaluated HERE, at the action. Selection stays neutral:
    // the same checkboxes drive email bulk send (uncapped, works on imported
    // invoices) -- never disable or filter them for QB reasons.
    const openQbPushConfirm = () => {
        const selected = invoices.filter(inv => selectedIds.has(inv.id));
        const eligible = [];
        const skipped = [];
        for (const inv of selected) {
            const check = computeQbPushEligibility(inv);
            if (check.eligible) eligible.push({ invoiceId: inv.id, invoiceNumber: inv.invoiceNumber, warning: check.warning });
            else skipped.push({ invoiceId: inv.id, invoiceNumber: inv.invoiceNumber, reason: check.reason });
        }
        setQbPushConfirm({
            eligible,
            skipped,
            warnCount: eligible.filter(e => e.warning).length,
            overCap: eligible.length > QB_PUSH_MAX,
        });
    };

    const handleQbBulkPush = async () => {
        const { eligible, skipped } = qbPushConfirm;
        setQbPushing(true);
        setQbPushProgress({ done: 0, total: eligible.length });
        const acc = { pushed: [], skipped: [...skipped], failed: [] };
        const functions = getFunctions();
        // qbcto-8d2b4f60 -- wait longer than the server can run (90s). Without this
        // the SDK's 70s default aborts a slow-but-succeeding chunk, showing the
        // operator an error while QuickBooks was actually written. Ceiling only --
        // a fast chunk still resolves the instant the server returns.
        const batchPush = httpsCallable(functions, 'batchPushInvoicesToQuickBooks', { timeout: 120000 });
        // Chunks run SEQUENTIALLY. A rejected call is a connection-level
        // failure (expired connection, no tax codes) that would fail every
        // later chunk identically -- stop the run and mark the rest
        // "Not attempted". Per-invoice failures come back inside
        // result.data.failed and do NOT stop the run (see the twin comment
        // in batchPushInvoicesToQuickBooks).
        for (let i = 0; i < eligible.length; i += QB_PUSH_CHUNK_SIZE) {
            const chunk = eligible.slice(i, i + QB_PUSH_CHUNK_SIZE);
            try {
                const result = await batchPush({ invoiceIds: chunk.map(e => e.invoiceId) });
                acc.pushed.push(...(result.data.pushed || []));
                acc.skipped.push(...(result.data.skipped || []));
                acc.failed.push(...(result.data.failed || []));
            } catch (err) {
                acc.failed.push(...chunk.map(e => ({ invoiceId: e.invoiceId, invoiceNumber: e.invoiceNumber, reason: err.message })));
                const remaining = eligible.slice(i + QB_PUSH_CHUNK_SIZE);
                acc.failed.push(...remaining.map(e => ({ invoiceId: e.invoiceId, invoiceNumber: e.invoiceNumber, reason: 'Not attempted - push stopped after an error' })));
                break;
            }
            setQbPushProgress({ done: Math.min(i + QB_PUSH_CHUNK_SIZE, eligible.length), total: eligible.length });
        }
        setQbPushing(false);
        setQbPushProgress(null);
        setQbPushConfirm(null);
        setQbPushResults(acc);
        setSelectedIds(new Set());
        setBatchMode(false);
    };

    const clearFilter = () => {
        navigate('/invoices');
        setSearchTerm('');
    };

    if (isLoading) return <div className="p-8 text-center text-gray-500 animate-pulse">Loading invoices...</div>;

    // colvis-5d1c8b0e -- the columns actually shown, in order. Today this only drops
    // the QB column when QuickBooks is not connected (structural availability). The
    // header, the body rows, and the empty-state colSpan all read from THIS list, so
    // the three can never disagree on how many columns exist.
    // colpick-6f4c2e83 -- visible = structurally available AND (locked OR not hidden by
    // the user). `col.locked ||` means a corrupt pref can never hide Inv #/Customer/Total.
    const visibleColumns = COLUMNS.filter(col =>
        (!col.available || col.available({ companySettings })) &&
        (col.locked || !hiddenIds.has(col.id))
    );

    return (
        <div className="p-3 sm:p-6 md:p-10 pb-20"> {/* pb-20 ensures last item isn't hidden by mobile nav if present */}
            
            {/* --- HEADER SECTION --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Invoices</h1>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        {filterParam === 'Unpaid' && (
                            <button onClick={() => navigate('/invoices')} className="flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded border border-blue-200 hover:bg-red-50 hover:text-red-600 transition-colors">
                                Filter: Unpaid <XCircle size={14} />
                            </button>
                        )}
                        <span className="text-xs text-gray-500">{sortedInvoices.length} record(s)</span>
                    </div>
                </div>
                <div className="w-full sm:w-auto flex flex-wrap gap-2 items-center">
                    <div className="relative w-full sm:w-56">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input type="text" placeholder="Search invoices..." value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                    </div>
                    {batchMode && selectedIds.size > 0 && canBatchInvoice(currentUserProfile) && ( /* s8portal-4e7b2c95 — server: batchSendInvoices gate */
                        <button onClick={() => setBatchSendModal(true)}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors">
                            <Send size={15} /> Send {selectedIds.size} Invoice{selectedIds.size !== 1 ? 's' : ''}
                        </button>
                    )}
                    {batchMode && selectedIds.size > 0 && companySettings?.quickbooksConnected && (
                        /* qbbulk-3e5b18c9 -- eligibility is NOT checked here; the confirm modal shows the real breakdown */
                        <button onClick={openQbPushConfirm}
                            className="flex items-center gap-2 px-3 py-2 bg-white text-green-700 border border-green-600 rounded-lg text-sm font-semibold hover:bg-green-50 transition-colors">
                            <ArrowUpRight size={15} /> Push {selectedIds.size} to QB
                        </button>
                    )}
                    {/* colpick-1d8a7b52 -- Columns picker; desktop only (mobile uses the card
                        view). Checkboxes reflect LOCAL hiddenIds (instant); the write is background. */}
                    <div className="relative hidden md:block" ref={columnMenuRef}>
                        <button onClick={() => setColumnMenuOpen(o => !o)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors
                                ${columnMenuOpen ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            title="Choose columns" aria-haspopup="true" aria-expanded={columnMenuOpen}>
                            <Columns3 size={15} /> Columns
                        </button>
                        {columnMenuOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-30 p-2">
                                <p className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Show columns</p>
                                {COLUMNS.filter(col => !col.available || col.available({ companySettings })).map(col => (
                                    <label key={col.id}
                                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm ${col.locked ? 'text-gray-400' : 'text-gray-700 hover:bg-gray-50 cursor-pointer'}`}>
                                        <input type="checkbox" disabled={col.locked}
                                            checked={col.locked || !hiddenIds.has(col.id)}
                                            onChange={() => toggleColumn(col.id)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span>{col.label}</span>
                                        {col.locked && <span className="ml-auto text-xs text-gray-400">Always shown</span>}
                                    </label>
                                ))}
                                <button onClick={resetColumns}
                                    className="w-full text-left px-2 py-1.5 mt-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded">
                                    Reset to default
                                </button>
                                {columnSaveError && (
                                    <p className="px-2 py-1 text-xs text-red-600">Couldn't save. Your columns will reset on reload.</p>
                                )}
                            </div>
                        )}
                    </div>
                    <button onClick={() => { setBatchMode(m => !m); setSelectedIds(new Set()); }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors
                            ${batchMode ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                        <CheckSquare size={15} /> {batchMode ? 'Cancel' : 'Select'}
                    </button>
                    <button className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                        onClick={() => openInvoiceModal(null)}>
                        <Plus size={18} /> New Invoice
                    </button>
                </div>
            </div>

            {/* --- FILTER BAR --- */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                {/* Date preset dropdown — matches Jobs view style */}
                <div className="flex items-center border rounded-md p-1 bg-white shadow-sm">
                    <select value={datePreset} onChange={e => setDatePreset(e.target.value)}
                        className="p-1 border-0 rounded-md text-sm font-medium text-gray-600 focus:ring-0 focus:outline-none bg-transparent appearance-none pr-8"
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")', backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}>
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="thisweek">This Week</option>
                        <option value="thismonth">This Month</option>
                        <option value="custom">Custom Range</option>
                    </select>
                </div>
                {datePreset === 'custom' && (
                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 border rounded-md p-1 bg-white shadow-sm">
                        <label className="text-sm font-medium text-gray-600 pl-1">From:</label>
                        <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                            className="p-1 border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" />
                        <label className="text-sm font-medium text-gray-600 pl-1">To:</label>
                        <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                            className="p-1 border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                )}
                {/* Status filters */}
                <div className="flex items-center gap-1 border-l pl-3 ml-1">
                    {['Draft','Sent','Unpaid','Partial','Past Due','Paid'].map(s => (
                        <button key={s} onClick={() => toggleStatus(s)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                                ${statusFilters.has(s) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            {s}
                        </button>
                    ))}
                    {statusFilters.size > 0 && (
                        <button onClick={() => setStatusFilters(new Set())} className="text-gray-400 hover:text-red-500 ml-1">
                            <XCircle size={14} />
                        </button>
                    )}
                </div>
                {/* qbattn-6f0b8c14 -- "Needs QB attention": failed push OR unsynced payment. Own
                    axis (ANDs with status pills), connected-orgs only, not persisted. flex-wrap
                    parent (:393) so it drops to its own line on mobile. */}
                {companySettings?.quickbooksConnected && (
                    <div className="flex items-center border-l pl-3 ml-1">
                        <button onClick={() => setQbAttentionOnly(v => !v)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                                ${qbAttentionOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            <AlertTriangle size={13} />
                            Needs QB attention
                        </button>
                    </div>
                )}
            </div>

            {/* --- DESKTOP VIEW (Hidden on Mobile) --- */}
            <div className="hidden md:block bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                        <tr>
                            {batchMode && (
                                <th className="px-4 py-3 w-10">
                                    <button onClick={() => handleSelectAll(
                                        sortedInvoices.every(i => selectedIds.has(i.id)) ? [] : sortedInvoices.map(i => i.id)
                                    )} className="text-gray-400 hover:text-blue-600">
                                        {sortedInvoices.length > 0 && sortedInvoices.every(i => selectedIds.has(i.id))
                                            ? <CheckSquare size={16} className="text-blue-600" />
                                            : <Square size={16} />}
                                    </button>
                                </th>
                            )}
                            {/* colhead-4c8b1d60 -- header from visibleColumns. Sortable columns
                                (col.sortKey set) get the click handler, the cursor-pointer +
                                hover affordance and the sort icon; a non-sortable column gets a
                                bare <th> with none of them. */}
                            {visibleColumns.map(col => (
                                col.sortKey
                                    ? <th key={col.id} className={`${col.thClass} cursor-pointer hover:bg-gray-100`} onClick={() => requestSort(col.sortKey)}>{col.label}{getSortIcon(col.sortKey)}</th>
                                    : <th key={col.id} className={col.thClass}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sortedInvoices.map(invoice => (
                            <tr key={invoice.id}
                                onClick={() => batchMode ? handleSelectInvoice(invoice.id, !selectedIds.has(invoice.id)) : onInvoiceSelect(invoice)}
                                className={`cursor-pointer transition-colors ${selectedIds.has(invoice.id) ? 'bg-blue-50' : 'bg-white hover:bg-blue-50'}`}>
                                {batchMode && (
                                    <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                                        <button onClick={() => handleSelectInvoice(invoice.id, !selectedIds.has(invoice.id))} className="text-gray-400 hover:text-blue-600">
                                            {selectedIds.has(invoice.id) ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} />}
                                        </button>
                                    </td>
                                )}
                                {/* colbody-2f9e6a4d -- cells from visibleColumns, same list and
                                    order as the header. tdClass may be a string or a function of
                                    the invoice (Balance is the only value-dependent class). */}
                                {visibleColumns.map(col => (
                                    <td key={col.id} className={typeof col.tdClass === 'function' ? col.tdClass(invoice) : col.tdClass}>{col.renderCell(invoice, { creditCustomerIds })}</td>
                                ))}
                            </tr>
                        ))}
                        {sortedInvoices.length === 0 && (
                            <tr>
                                {/* qbattn-3d9e4a72 colspan-9b7e3a12 -- desktop had no empty-state row
                                    (mobile does), so a zero-match filter rendered a bare header. colSpan is derived from
                                    visibleColumns (the same list the header renders) + the batch column,
                                    so it can never drift out of sync when a column is added or hidden. */}
                                <td colSpan={visibleColumns.length + (batchMode ? 1 : 0)} className="text-center py-10 text-gray-500">
                                    No invoices match the current filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- MOBILE CARD VIEW (Visible Only on Mobile) --- */}
            <div className="md:hidden space-y-3">
                {sortedInvoices.map(invoice => (
                    <div key={invoice.id}
                        onClick={() => batchMode ? handleSelectInvoice(invoice.id, !selectedIds.has(invoice.id)) : onInvoiceSelect(invoice)}
                        className={`p-4 rounded-xl shadow-sm border active:scale-[0.99] transition-all cursor-pointer relative
                            ${selectedIds.has(invoice.id) ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}`}
                    >
                        {batchMode && (
                            <div className="absolute top-4 left-4">
                                {selectedIds.has(invoice.id)
                                    ? <CheckSquare size={18} className="text-blue-600" />
                                    : <Square size={18} className="text-gray-400" />}
                            </div>
                        )}
                        {/* Top Row: Invoice # and Status */}
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Invoice #{invoice.invoiceNumber}</span>
                                <h3 className="text-lg font-bold text-gray-900 leading-tight mt-0.5">
                                    {invoice.customerDetails?.name || invoice.customerName || 'Unknown Customer'}
                                    {creditCustomerIds.has(invoice.customerId) && <CreditChip />}
                                </h3>
                            </div>
                            <InvoiceStatusBadge status={invoice.status} />
                        </div>

                        {/* Middle Row: Dates and Amounts */}
                        <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                                <p className="text-xs text-gray-500 flex items-center gap-1"><Calendar size={12}/> Due Date</p>
                                <p className="text-sm font-medium text-gray-700">{formatDate(invoice.dueDate)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500 flex items-center gap-1 justify-end"><DollarSign size={12}/> Total</p>
                                <p className="text-sm font-medium text-gray-900">{formatCurrency(invoice.total)}</p>
                            </div>
                        </div>

                        {/* r3-paiddate — Paid Row: Date Paid and Amount Paid */}
                        <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                                <p className="text-xs text-gray-500 flex items-center gap-1"><Calendar size={12}/> Date Paid</p>
                                <p className="text-sm font-medium text-gray-700">{invoice.paidDate ? formatDate(invoice.paidDate) : '—'}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500 flex items-center gap-1 justify-end"><DollarSign size={12}/> Amount Paid</p>
                                <p className="text-sm font-medium text-green-600">{formatCurrency(invoice.amountPaid)}</p>
                            </div>
                        </div>

                        {/* QuickBooks push state -- mirrors the desktop QB column. qbcol-6d24e9b7 */}
                        {companySettings?.quickbooksConnected && (
                            <div className="mb-3">
                                <p className="text-xs text-gray-500">QuickBooks</p>
                                <div className="mt-1">{renderQbStateBadge(invoice.qbPushState, invoice.qbPushError, qbMismatchWordsFor(invoice))}</div>
                                {/* paysyncmob-3e9b5a72 -- inline payment-sync flag; touch has no hover */}
                                {isPaymentSyncWarningActive(invoice) && (
                                    <p className="text-xs text-amber-600 mt-1">A recorded payment has not reached QuickBooks yet.</p>
                                )}
                                {/* qbfail-9d54c7e1 -- inline reason: no hover on touch */}
                                {invoice.qbPushState === 'failed' && invoice.qbPushError && (
                                    <p className="text-xs text-red-600 mt-1">{invoice.qbPushError}</p>
                                )}
                            </div>
                        )}

                        {/* Bottom Row: Balance Bar (Visual Indicator) */}
                        <div className="mt-2 pt-3 border-t border-gray-100 flex justify-between items-center">
                            <span className="text-xs text-gray-500">Balance Due</span>
                            <span className={`text-base font-bold ${invoice.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {formatCurrency(invoice.balanceDue)}
                            </span>
                        </div>
                    </div>
                ))}
                
                {sortedInvoices.length === 0 && (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-500">No invoices found.</p>
                        <button onClick={clearFilter} className="text-blue-600 text-sm font-medium mt-2">Clear Filters</button>
                    </div>
                )}
            </div>

            {/* --- BATCH SEND MODAL --- */}
            {batchSendModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-2">Send {selectedIds.size} Invoice{selectedIds.size !== 1 ? 's' : ''}?</h2>
                        <p className="text-sm text-gray-500 mb-6">Each customer will receive their own email with a secure payment link. Invoices will be marked as <strong>Sent</strong>.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setBatchSendModal(false)} disabled={batchLoading}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                            <button onClick={handleBatchSend} disabled={batchLoading}
                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                                {batchLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={15} />}
                                {batchLoading ? 'Sending...' : 'Send All'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- BATCH RESULTS MODAL --- */}
            {batchResults && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-4">Batch Send Complete</h2>
                        <div className="space-y-3 mb-6">
                            <div className="flex items-center gap-3 bg-green-50 rounded-lg p-3">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-green-700 font-bold text-sm">{batchResults.sent?.length || 0}</span>
                                </div>
                                <span className="text-sm text-green-700 font-medium">Invoice{batchResults.sent?.length !== 1 ? 's' : ''} sent successfully</span>
                            </div>
                            {batchResults.failed?.length > 0 && (
                                <div className="bg-red-50 rounded-lg p-3">
                                    <p className="text-sm text-red-700 font-medium mb-2">{batchResults.failed.length} failed:</p>
                                    {batchResults.failed.map((f, i) => (
                                        <p key={i} className="text-xs text-red-500">• {f.reason}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button onClick={() => setBatchResults(null)}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">Done</button>
                    </div>
                </div>
            )}

            {/* --- QB PUSH CONFIRM MODAL (qbbulk-c58e7f02) --- */}
            {qbPushConfirm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto">
                        <h2 className="text-lg font-bold text-gray-800 mb-2">Push to QuickBooks</h2>
                        {qbPushConfirm.overCap ? (
                            <p className="text-sm text-red-600 mb-4">
                                You have selected {qbPushConfirm.eligible.length} eligible invoices.
                                The limit is {QB_PUSH_MAX} per push - narrow your selection and try again.
                            </p>
                        ) : qbPushConfirm.eligible.length === 0 ? (
                            <p className="text-sm text-gray-700 font-medium mb-4">None of the selected invoices can be pushed to QuickBooks.</p>
                        ) : (
                            <p className="text-sm text-gray-600 mb-4">
                                <strong>{qbPushConfirm.eligible.length} of {qbPushConfirm.eligible.length + qbPushConfirm.skipped.length}</strong> selected invoices will be pushed to QuickBooks.
                            </p>
                        )}
                        {!qbPushConfirm.overCap && qbPushConfirm.skipped.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-1">
                                {qbPushConfirm.skipped.map((s, i) => (
                                    <p key={i} className="text-xs text-gray-600">#{s.invoiceNumber || '?'} skipped - {s.reason}</p>
                                ))}
                            </div>
                        )}
                        {!qbPushConfirm.overCap && qbPushConfirm.warnCount > 0 && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                {qbPushConfirm.warnCount} of the {qbPushConfirm.eligible.length} pushable invoices ha{qbPushConfirm.warnCount !== 1 ? 've' : 's'} no
                                customer email. QuickBooks will match them by customer name, which can attach to the wrong customer. They will still be pushed.
                            </p>
                        )}
                        <div className="flex gap-3">
                            <button onClick={() => setQbPushConfirm(null)} disabled={qbPushing}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
                                {qbPushConfirm.overCap || qbPushConfirm.eligible.length === 0 ? 'Close' : 'Cancel'}
                            </button>
                            {!qbPushConfirm.overCap && qbPushConfirm.eligible.length > 0 && (
                                <button onClick={handleQbBulkPush} disabled={qbPushing}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                                    {qbPushing ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <ArrowUpRight size={15} />}
                                    {qbPushing && qbPushProgress ? `Pushing ${qbPushProgress.done} of ${qbPushProgress.total}...` : 'Push to QuickBooks'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* --- QB PUSH RESULTS MODAL (qbbulk-1d90b3a7) --- */}
            {qbPushResults && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto">
                        <h2 className="text-lg font-bold text-gray-800 mb-4">QuickBooks Push Complete</h2>
                        <div className="space-y-3 mb-6">
                            <div className="bg-green-50 rounded-lg p-3">
                                <p className="text-sm text-green-700 font-medium">
                                    {qbPushResults.pushed.length} pushed{qbPushResults.pushed.length > 0 ? ': ' : ''}
                                    {qbPushResults.pushed.map(p => '#' + (p.invoiceNumber || '?')).join(', ')}
                                </p>
                                {qbPushResults.pushed.filter(p => p.warning).map((p, i) => (
                                    <p key={i} className="text-xs text-amber-700 mt-1">#{p.invoiceNumber || '?'}: {p.warning}</p>
                                ))}
                            </div>
                            {qbPushResults.skipped.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <p className="text-sm text-gray-700 font-medium mb-1">{qbPushResults.skipped.length} skipped:</p>
                                    {qbPushResults.skipped.map((s, i) => (
                                        <p key={i} className="text-xs text-gray-600">#{s.invoiceNumber || '?'} - {s.reason}</p>
                                    ))}
                                </div>
                            )}
                            {qbPushResults.failed.length > 0 && (
                                <div className="bg-red-50 rounded-lg p-3">
                                    <p className="text-sm text-red-700 font-medium mb-1">{qbPushResults.failed.length} failed:</p>
                                    {qbPushResults.failed.map((f, i) => (
                                        <p key={i} className="text-xs text-red-500">#{f.invoiceNumber || '?'} - {f.reason}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button onClick={() => setQbPushResults(null)}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">Done</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoicesView;